import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, MonoLabel } from "./shared";
import { LQIP } from "./lqip";

const PORTRAIT = "/photos/amos-brake-work-web.jpg";
const ACTION = "/photos/rav4-lift-web.jpg";

const FACTS = [
  ["Technician", "Amos Olayemi"],
  ["Shop", "His Grace Auto Repair"],
  ["Location", "580 Jersey Ave, Unit 2"],
  ["Contact", "amos.olayemi@yahoo.com"],
];

export default function Technician() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-30, 30]);

  return (
    <section
      id="technician"
      ref={ref}
      className="relative overflow-hidden border-y border-line/70 bg-panel/40 py-24 lg:py-32"
    >
      <div className="mx-auto grid max-w-[1500px] grid-cols-1 items-center gap-14 px-5 sm:px-8 md:grid-cols-12 lg:px-14">
        <Reveal className="relative md:col-span-5" y={40}>
          <motion.div style={{ y }} className="relative">
            <div
              data-testid="team-lead-technician-card"
              className="group relative"
            >
              <div
                className="absolute -inset-0 translate-x-3 translate-y-3 border border-gold/60 transition-transform duration-500 group-hover:translate-x-4 group-hover:translate-y-4"
                aria-hidden="true"
              />
              <div
                className="relative aspect-[4/5] w-full overflow-hidden border border-line bg-panel bg-cover bg-center"
                style={{ backgroundImage: `url(${LQIP[PORTRAIT]})` }}
              >
                <img
                  src={PORTRAIT}
                  width={1000}
                  height={1333}
                  decoding="async"
                  alt="Amos Olayemi working on a brake job at His Grace Auto Repair"
                  loading="lazy"
                  className="block h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-[1.02] group-hover:grayscale-0"
                />
              </div>
            </div>
            <div className="absolute -bottom-10 -right-4 hidden aspect-square w-44 overflow-hidden border border-line bg-panel shadow-2xl sm:block lg:-right-8 lg:w-52">
              <img
                src={ACTION}
                width={700}
                height={525}
                decoding="async"
                alt="A Toyota RAV4 on the lift at His Grace Auto Repair"
                loading="lazy"
                className="block h-full w-full object-cover"
              />
            </div>
          </motion.div>
          <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-zinc-300 sm:mt-16">
            In the bay — Jersey Ave, New Brunswick
          </p>
        </Reveal>

        <div className="md:col-span-7 md:pl-6 lg:pl-16">
          <Reveal>
            <MonoLabel>The Craftsman</MonoLabel>
            <h2 className="mt-4 font-display text-5xl uppercase leading-[0.9] tracking-wide sm:text-6xl lg:text-7xl">
              Meet Your <span className="text-gold">Technician</span>
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-8 font-display text-3xl uppercase tracking-wide text-zinc-100 sm:text-4xl">
              Amos Olayemi
            </p>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-zinc-300">
              His Grace Auto Repair is the workshop of auto technician Amos
              Olayemi. Every vehicle that rolls into the bay on Jersey Avenue
              gets his personal attention — no hand-offs, no guesswork: a
              proper diagnosis, a plain explanation and a repair you can trust
              on the road.
            </p>
          </Reveal>

          <Reveal delay={0.18}>
            <blockquote className="mt-9 border-l-2 border-gold pl-6">
              <p className="font-display text-2xl uppercase leading-snug tracking-wide text-zinc-50 sm:text-3xl">
                “Customer satisfaction is our priority.”
              </p>
              <cite className="mt-3 block font-mono text-xs not-italic uppercase tracking-[0.25em] text-gold">
                — The house motto
              </cite>
            </blockquote>
          </Reveal>

          <Reveal delay={0.25}>
            <dl className="mt-10 grid grid-cols-1 gap-px border border-line bg-line sm:grid-cols-2">
              {FACTS.map(([k, v]) => (
                <div key={k} className="bg-void px-6 py-4">
                  <dt className="font-mono text-xs uppercase tracking-[0.22em] text-zinc-300">
                    {k}
                  </dt>
                  <dd className="mt-1.5 font-cond text-base font-semibold uppercase tracking-[0.08em] text-zinc-100">
                    {v}
                  </dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
